import { useState, useContext } from 'react';

import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx'
import { DeleteOutlined } from '@ant-design/icons';

import { industryDetails, capTableRenderFields, shareHoldersRenderFields, investorSplitRenderFields } from '../../constants.jsx';
import { renderFormRow, genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';

async function saveEntityToDatabase(entity) {
    return genericPOSTDatabaseCall('/api/cfi/saveEntity', { entity }, 'entity info');
}

const handleInputBlur = (id, field, value, messageApi, callback) => {
    let newValue = value;

    if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
        messageApi.error(field.errorMessage);
        newValue = field.default || ''; // Use default value if provided
    }

    if (newValue === '') {
        messageApi.error('This field cannot be empty');
        newValue = field.default || ''; // Use default value if provided
    }
    callback(id, field, newValue);
}

function CapTableShareholdersTable({ entitySelected, setEntitySelected, messageApi }) {
    const [newShareholderName, setNewShareholderName] = useState('');

    const addShareholder = async () => {
        if (newShareholderName.trim() === '') {
            messageApi.error('Shareholder name cannot be empty');
            return;
        };
        messageApi.loading('Adding shareholder...', 0);
        try {
            const shareholderData = {
                shareholder: newShareholderName,
                share_percentage: 0 // Default percentage, can be updated later
            };
            const response = await genericPOSTDatabaseCall('/api/cfi/addShareholder', { entityId: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id , shareholder: shareholderData }, 'shareholder');
            setEntitySelected(prev => ({
                ...prev,
                shareholders: [...prev.shareholders, response]
            }));
            setNewShareholderName('');
            messageApi.destroy();
            messageApi.success('Shareholder added successfully');
        } catch (error) {
            console.error('Error adding shareholder:', error);
            messageApi.destroy();
            messageApi.error('Failed to add shareholder');
        }
    }

    const deleteShareholder = async (shareholder) => {
        messageApi.loading('Delete shareholder...', 0);
        try {
            await genericDELETEDatabaseCall('/api/cfi/deleteShareholder', { shareholder }, 'shareholder');
            setEntitySelected(prev => ({
                ...prev,
                shareholders: prev.shareholders.filter(s => s.id !== shareholder.id)
            }));
            messageApi.destroy();
            messageApi.success('Shareholder deleted successfully');
        } catch (error) {
            console.error('Error deleting shareholder:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete shareholder');
        }
    }

    const saveShareholders = async () => {
        messageApi.loading('Saving shareholders...', 0);
        try {
            await genericPOSTDatabaseCall('/api/cfi/updateShareholders', { shareholders: entitySelected.shareholders }, 'shareholders');
            messageApi.destroy();
            messageApi.success('Shareholders saved successfully');
        } catch (error) {
            console.error('Error saving shareholders:', error);
            messageApi.destroy();
            messageApi.error('Failed to save shareholders');
        }
    }

    const handleShareholderChange = (id, field, value) => {
        const updatedShareholders = [...entitySelected.shareholders];
        const index = updatedShareholders.findIndex(shareholder => shareholder.id === id);

        if (index !== -1) {
            updatedShareholders[index] = {
                ...updatedShareholders[index],
                [field.key]: value
            };
        }

        setEntitySelected(prev => ({
            ...prev,
            shareholders: updatedShareholders
        }));
    }

    return (
        <div className="add-entity-controls form-section">
            <h3 className='margin-zero'>Shareholders</h3>
            <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                <table className="entity-table margin-zero">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Shareholders</th>
                            <th>Share Percentage</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entitySelected.shareholders.map((item) => (
                            <tr key={item.id}>
                                <td style={{ padding: '14px', textAlign: 'center' }}>
                                    <DeleteOutlined onClick={() => deleteShareholder(item)} style={{ color: 'red', fontSize: 16, cursor: 'pointer' }} />
                                </td>
                                {shareHoldersRenderFields.map((field) => (
                                    <td>
                                        <input
                                            onBlur={(event) => handleInputBlur(item.id, field, event.target.value, messageApi, handleShareholderChange)}
                                            onChange={(event) => handleShareholderChange(item.id, field, event.target.value)}
                                            style={{ width: '90%' }} className='form-input' name={`shareholder_${item.id}`} type='text' value={item[field.key]} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="form-row">
                <label htmlFor="new_shareholder_name">Add New Shareholder</label>
                <input type="text" className="form-input" value={newShareholderName} onChange={(e) => setNewShareholderName(e.target.value)} placeholder="Enter shareholder name" id="new_shareholder_name" />
            </div>
            <button className="add-button" onClick={addShareholder}>ADD Shareholder</button>
            <button className="save-button" onClick={saveShareholders}>Save all shareholders</button>
        </div>
    )
}

function CapTableInvestorSplitsTable({ entitySelected, setEntitySelected, messageApi }) {
    const addInvestorSplit = async () => {
        messageApi.loading('Adding investor split...', 0);
        try {
            const response = await genericPOSTDatabaseCall('/api/cfi/addInvestorSplit', { entityId: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id }, 'investor split');
            setEntitySelected(prev => ({
                ...prev,
                investorSplits: [...prev.investorSplits, response]
            }));
            messageApi.destroy();
            messageApi.success('Investor split added successfully');
        } catch (error) {
            console.error('Error adding investor split:', error);
            messageApi.destroy();
            messageApi.error('Failed to add investor split');
        }
    };

    const deleteInvestorSplit = async (investorSplit) => {
        messageApi.loading('Deleting investor split...', 0);
        try {
            await genericDELETEDatabaseCall('/api/cfi/deleteInvestorSplit', { investorSplit }, 'investor split');
            setEntitySelected(prev => ({
                ...prev,
                investorSplits: prev.investorSplits.filter(split => split.id !== investorSplit.id)
            }));
            messageApi.destroy();
            messageApi.success('Investor split deleted successfully');
        } catch (error) {
            console.error('Error deleting investor split:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete investor split');
        }
    }

    const handleInvestorSplitChange = (id, field, value) => {
        const updatedInvestorSplits = [...entitySelected.investorSplits];
        const index = updatedInvestorSplits.findIndex(split => split.id === id);

        if (index !== -1) {
            updatedInvestorSplits[index] = {
                ...updatedInvestorSplits[index],
                [field.key]: value
            };
        }

        setEntitySelected(prev => ({
            ...prev,
            investorSplits: updatedInvestorSplits
        }));
    }

    const saveInvestorSplits = async () => {
        messageApi.loading('Saving investor splits...', 0);
        try {
            genericPOSTDatabaseCall('/api/cfi/updateInvestorSplits', { investorSplits: entitySelected.investorSplits }, 'investor splits');
            messageApi.destroy();
            messageApi.success('Investor splits saved successfully');
        } catch (error) {
            console.error('Error saving investor splits:', error);
            messageApi.destroy();
            messageApi.error('Failed to save investor splits');
        }
    };

    return (
        <div className="form-section add-entity-controls">
            <h3 className='margin-zero'>Cap Table Investors Splits</h3>
            <table className="entity-table margin-zero">
                <thead>
                    <tr>
                        <th></th>
                        <th>Investor #</th>
                        <th>Share split</th>
                    </tr>
                </thead>
                <tbody>
                    {entitySelected.investorSplits.map((split, index) => (
                        <tr key={index}>
                            <td style={{ padding: '14px', textAlign: 'center' }}>
                                <DeleteOutlined onClick={() => deleteInvestorSplit(split)} style={{ color: 'red', fontSize: 16, cursor: 'pointer' }} />
                            </td>
                            <td>
                                <input style={{ width: '90%' }} className='form-input' type='text' value={split.investor_name} 
                                    onBlur={(event) => handleInputBlur(split.id, investorSplitRenderFields[0], event.target.value, messageApi, handleInvestorSplitChange)}
                                    onChange={(event) => handleInvestorSplitChange(split.id, investorSplitRenderFields[0], event.target.value)} />
                            </td>
                            <td>
                                <input style={{ width: '90%' }} className='form-input' type='text' value={split.split_percentage} 
                                    onBlur={(event) => handleInputBlur(split.id, investorSplitRenderFields[1], event.target.value, messageApi, handleInvestorSplitChange)}
                                    onChange={(event) => handleInvestorSplitChange(split.id, investorSplitRenderFields[1], event.target.value)} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button onClick={addInvestorSplit} className="add-button">ADD Investor Split</button>
            <button onClick={saveInvestorSplits} className="save-button">Save all investor splits</button>
        </div>
    )
}

function CapTableSettingsModal({contextToUse}) {
    // Use the provided context or default to CriticalFinancePageContext
    const { entitySelected, messageApi, setEntitySelected } = useContext(contextToUse || CriticalFinancePageContext);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        messageApi.loading('Saving entity info...', 0);
        try {
            await saveEntityToDatabase({...entitySelected, id: entitySelected.cfi_id !== undefined ? entitySelected.cfi_id : entitySelected.id});
            messageApi.destroy();
            messageApi.success('Company info saved successfully');
        } catch (error) {
            console.error('Error saving entity info:', error);
            messageApi.destroy();
            messageApi.error('Failed to save entity info');
        }
        setSaving(false);
    };

    const handleInputChange = (event, entity, key, field) => {
        let newValue = event.target.value;

        setEntitySelected(prev => ({
            ...prev,
            [key]: newValue
        }));
    }

    const handleInputBlur = (event, entity, key, field) => {
        let newValue = event.target.value;

        if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
            messageApi.error(field.errorMessage);
            newValue = industryDetails[entity.industry][key] || field.default || ''; // Use default value if provided
        }

        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = industryDetails[entity.industry][key] || field.default || ''; // Use default value if provided
        }

        setEntitySelected(prev => ({
            ...prev,
            [key]: newValue
        }));
    }

    return (
        <div className="other-variables-cost financial-builder-container entity-industry-container">
            <h2>Investment & Equity Pricing</h2>
            <div className='entity-card'>
                {capTableRenderFields.map(field => renderFormRow(field, entitySelected, handleInputChange, true, handleInputBlur))}
                <div className="flex-row bottom-actions">
                    <div className="form-row actions-row">
                        <button disabled={saving} onClick={handleSave} type="button" className="save-button">Save</button>
                    </div>
                </div>
                <CapTableShareholdersTable messageApi={messageApi} entitySelected={entitySelected} setEntitySelected={setEntitySelected} />
                <CapTableInvestorSplitsTable entitySelected={entitySelected} setEntitySelected={setEntitySelected} messageApi={messageApi} />
            </div>
        </div>
    );
}

export default CapTableSettingsModal;
